import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "session-overlays" }
const _hoisted_2 = { class: "p-4 text-center" }
const _hoisted_3 = { class: "mb-4" }
const _hoisted_4 = { class: "flex-center" }

import { reactive, ref, computed, watch, onUnmounted, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { jwtDecode, type JwtPayload } from "jwt-decode";

import { ConfirmDialog, Modal, Button } from "@/components/global";

import { PUBLIC_AUTH_ROUTE_NAMES } from "@/router";
import TokenService from "@/api.resource/token.service";

const idleWarningDuration = 1000 * 60;
const tokenWarningDuration = 1000 * 60;


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const store = useStore();
const route = useRoute();

const MCAdminCognitoID = computed<string>(() => store.getters["auth/MCAdminCognitoID"]);
const userMail = computed<string>(() => store.getters["auth/userMail"]);
const accessToken = computed<string | null>(() => store.getters["auth/accessToken"]);
const isLoggedIn = computed(() => !!TokenService.getAccessToken() || !!accessToken.value);
const isPublicAuthRoute = computed(() => PUBLIC_AUTH_ROUTE_NAMES.includes(route.name as string));
const isTimeoutTrackingEnabled = computed(() => isLoggedIn.value && !isPublicAuthRoute.value);

const checkIdleState = reactive<{
  isIdle: boolean;
  timeoutId: ReturnType<typeof setTimeout> | null;
  idleTimeout: number;
}>({
  isIdle: false,
  timeoutId: null,
  idleTimeout: 1000 * 60 * 15
});

const showIdleDialog = ref(false);
const showTokenDialog = ref(false);
const showTimeoutModal = ref(false);
const timeoutModalEligible = ref(false);
const timeoutReason = ref<"inactivity" | "expired" | null>(null);
const isRenewingSession = ref(false);
const isLoggingOutSession = ref(false);

const idleCountdown = ref(60);
const tokenCountdown = ref(60);

const idleWarningTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
const idleLogoutTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
const idleCountdownIntervalId = ref<ReturnType<typeof setInterval> | null>(null);

const accessTokenExpiryTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const tokenWarningTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
const tokenExpiryTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
const tokenCountdownIntervalId = ref<ReturnType<typeof setInterval> | null>(null);

const formatSeconds = (seconds: number) => {
  const safeSeconds = Math.max(seconds, 0);
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

const idleDialogSubMessage = computed(() => `Time remaining: ${formatSeconds(idleCountdown.value)}`);

const tokenDialogSubMessage = computed(() => `Time remaining: ${formatSeconds(tokenCountdown.value)}`);
const isTokenActionLoading = computed(() => isRenewingSession.value || isLoggingOutSession.value);
const isIdleActionLoading = computed(() => isLoggingOutSession.value);

const timeoutMessage = computed(() => {
  if (timeoutReason.value === "expired") {
    return "You've been logged out due to expired session.";
  }
  return "You've been logged out due to inactivity.";
});

const clearIdleTimers = () => {
  if (checkIdleState.timeoutId) {
    clearTimeout(checkIdleState.timeoutId);
    checkIdleState.timeoutId = null;
  }

  if (idleWarningTimeoutId.value) {
    clearTimeout(idleWarningTimeoutId.value);
    idleWarningTimeoutId.value = null;
  }

  if (idleLogoutTimeoutId.value) {
    clearTimeout(idleLogoutTimeoutId.value);
    idleLogoutTimeoutId.value = null;
  }

  if (idleCountdownIntervalId.value) {
    clearInterval(idleCountdownIntervalId.value);
    idleCountdownIntervalId.value = null;
  }
};

const startIdleCountdown = (seconds: number) => {
  idleCountdown.value = seconds;
  showIdleDialog.value = true;

  if (idleCountdownIntervalId.value) {
    clearInterval(idleCountdownIntervalId.value);
  }

  idleCountdownIntervalId.value = setInterval(() => {
    idleCountdown.value -= 1;
    if (idleCountdown.value <= 0) {
      if (idleCountdownIntervalId.value) {
        clearInterval(idleCountdownIntervalId.value);
        idleCountdownIntervalId.value = null;
      }
    }
  }, 1000);
};

const resetTimer = () => {
  if (!isTimeoutTrackingEnabled.value) return;
  clearIdleTimers();
  showIdleDialog.value = false;
  checkIdleState.isIdle = false;

  const timeUntilWarning = checkIdleState.idleTimeout - idleWarningDuration;
  const warningDelay = Math.max(timeUntilWarning, 0);

  idleWarningTimeoutId.value = setTimeout(() => {
    checkIdleState.isIdle = true;
    const countdownSeconds = Math.ceil(Math.min(idleWarningDuration, checkIdleState.idleTimeout) / 1000);
    startIdleCountdown(countdownSeconds);
  }, warningDelay);

  idleLogoutTimeoutId.value = setTimeout(() => {
    handleAutoLogout("inactivity");
  }, checkIdleState.idleTimeout);
};

const onActivity = () => {
  if (!isTimeoutTrackingEnabled.value) return;
  if (showIdleDialog.value || showTokenDialog.value || showTimeoutModal.value) return;
  checkIdleState.isIdle = false;
  resetTimer();
};

const decodeTokenExp = (token: string | null): number | null => {
  if (!token) return null;
  try {
    const decodedToken = jwtDecode<JwtPayload>(token);
    if (!decodedToken.exp) return null;
    return decodedToken.exp * 1000;
  } catch {
    return null;
  }
};

const clearTokenTimers = () => {
  if (accessTokenExpiryTimeout.value) {
    clearTimeout(accessTokenExpiryTimeout.value);
    accessTokenExpiryTimeout.value = null;
  }

  if (tokenWarningTimeoutId.value) {
    clearTimeout(tokenWarningTimeoutId.value);
    tokenWarningTimeoutId.value = null;
  }

  if (tokenExpiryTimeoutId.value) {
    clearTimeout(tokenExpiryTimeoutId.value);
    tokenExpiryTimeoutId.value = null;
  }

  if (tokenCountdownIntervalId.value) {
    clearInterval(tokenCountdownIntervalId.value);
    tokenCountdownIntervalId.value = null;
  }
};

const startTokenCountdown = (seconds: number) => {
  tokenCountdown.value = Math.max(seconds, 0);
  showTokenDialog.value = true;

  if (tokenCountdownIntervalId.value) {
    clearInterval(tokenCountdownIntervalId.value);
  }

  tokenCountdownIntervalId.value = setInterval(() => {
    tokenCountdown.value -= 1;
    if (tokenCountdown.value <= 0) {
      tokenCountdown.value = 0;
      if (tokenCountdownIntervalId.value) {
        clearInterval(tokenCountdownIntervalId.value);
        tokenCountdownIntervalId.value = null;
      }
    }
  }, 1000);
};

const handleAccessTokenExpiry = async () => {
  if (!isTimeoutTrackingEnabled.value) return;

  const refreshToken = TokenService.getRefreshToken();
  const refreshTokenExp = decodeTokenExp(refreshToken);

  if (!refreshTokenExp || refreshTokenExp <= Date.now()) {
    clearTokenTimers();
    const countdownSeconds = Math.ceil(tokenWarningDuration / 1000);
    startTokenCountdown(countdownSeconds);
    tokenExpiryTimeoutId.value = setTimeout(() => {
      handleAutoLogout("expired");
    }, countdownSeconds * 1000);
    return;
  }

  try {
    await store.dispatch("auth/REFRESH_TOKEN");
  } catch {
    const remainingMs = refreshTokenExp - Date.now();
    if (remainingMs <= 0) {
      handleAutoLogout("expired");
      return;
    }

    clearTokenTimers();
    const countdownSeconds = Math.ceil(Math.min(tokenWarningDuration, remainingMs) / 1000);
    startTokenCountdown(countdownSeconds);
    tokenExpiryTimeoutId.value = setTimeout(() => {
      handleAutoLogout("expired");
    }, remainingMs);
  }
};

const handleIdleStaySignedIn = () => {
  if (isIdleActionLoading.value) return;
  showIdleDialog.value = false;
  checkIdleState.isIdle = false;
  resetTimer();
};

const handleIdleSignOut = async () => {
  if (isIdleActionLoading.value) return;
  isLoggingOutSession.value = true;
  showIdleDialog.value = false;
  timeoutModalEligible.value = false;
  showTimeoutModal.value = false;

  try {
    await store.dispatch("auth/LOGOUT");
  } finally {
    isLoggingOutSession.value = false;
  }
};

const handleTokenRenew = async () => {
  if (isTokenActionLoading.value) return;
  isRenewingSession.value = true;

  try {
    await store.dispatch("auth/REFRESH_TOKEN");
    showTokenDialog.value = false;
  } catch {
    handleAutoLogout("expired");
  } finally {
    isRenewingSession.value = false;
  }
};

const handleTokenSignOut = async () => {
  if (isTokenActionLoading.value) return;
  isLoggingOutSession.value = true;
  showTokenDialog.value = false;
  timeoutModalEligible.value = false;
  showTimeoutModal.value = false;

  try {
    await store.dispatch("auth/LOGOUT");
  } finally {
    isLoggingOutSession.value = false;
  }
};

const handleTimeoutModalClose = () => {
  showTimeoutModal.value = false;
  timeoutModalEligible.value = false;
  window.location.href = "/login";
};

const handleAutoLogout = async (reason: "inactivity" | "expired") => {
  if (!isTimeoutTrackingEnabled.value) return;
  isRenewingSession.value = false;
  isLoggingOutSession.value = false;
  clearIdleTimers();
  clearTokenTimers();
  showIdleDialog.value = false;
  showTokenDialog.value = false;
  timeoutModalEligible.value = true;
  showTimeoutModal.value = true;
  timeoutReason.value = reason;

  await store.dispatch("auth/LOGOUT", { redirect: false, force: true });
};

watch(() => accessToken.value, value => {
  clearTokenTimers();
  if (!isTimeoutTrackingEnabled.value) {
    showTokenDialog.value = false;
    return;
  }

  if (value) {
    const accessTokenExp = decodeTokenExp(value);
    if (accessTokenExp && accessTokenExp > Date.now()) {
      accessTokenExpiryTimeout.value = setTimeout(() => {
        handleAccessTokenExpiry();
      }, accessTokenExp - Date.now());
    }

    const refreshToken = TokenService.getRefreshToken();
    const refreshTokenExp = decodeTokenExp(refreshToken);
    if (refreshTokenExp && refreshTokenExp > Date.now()) {
      const timeUntilExpiry = refreshTokenExp - Date.now();
      const warningDelay = Math.max(timeUntilExpiry - tokenWarningDuration, 0);
      tokenWarningTimeoutId.value = setTimeout(() => {
        const countdownSeconds = Math.ceil(Math.min(tokenWarningDuration, timeUntilExpiry) / 1000);
        startTokenCountdown(countdownSeconds);
      }, warningDelay);

      tokenExpiryTimeoutId.value = setTimeout(() => {
        handleAutoLogout("expired");
      }, timeUntilExpiry);
    } else if (refreshTokenExp && refreshTokenExp <= Date.now()) {
      handleAutoLogout("expired");
    }
  }
}, { immediate: true, deep: true });

watch(() => isTimeoutTrackingEnabled.value, enabled => {
  if (enabled) {
    resetTimer();
    return;
  }

  isRenewingSession.value = false;
  isLoggingOutSession.value = false;

  clearIdleTimers();
  clearTokenTimers();

  showIdleDialog.value = false;
  showTokenDialog.value = false;

  if (!timeoutModalEligible.value) {
    showTimeoutModal.value = false;
    timeoutReason.value = null;
  }
}, { immediate: true });

watch(() => checkIdleState.isIdle, value => {
  if (isTimeoutTrackingEnabled.value && MCAdminCognitoID.value !== "" && userMail.value !== "" && value) {
    showIdleDialog.value = true;
  }
});

onMounted(() => {
  if (isTimeoutTrackingEnabled.value) {
    resetTimer();
  }

  window.addEventListener("mousemove", onActivity);
  window.addEventListener("keydown", onActivity);

  store.dispatch("settings/USER_AGREEMENT_HTML_STRING", "HTML");
});

onUnmounted(() => {
  clearIdleTimers();
  clearTokenTimers();

  window.removeEventListener("mousemove", onActivity);
  window.removeEventListener("keydown", onActivity);
});

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_router_view),
    _createElementVNode("div", _hoisted_1, [
      (showIdleDialog.value)
        ? (_openBlock(), _createBlock(_unref(ConfirmDialog), {
            key: 0,
            title: "You will be logged out soon",
            message: "For your security, we log you out automatically when you have been inactive for a certain period of time.",
            subMessage: idleDialogSubMessage.value,
            cancelText: "Log out now",
            confirmText: "Continue session",
            confirmButtonVariant: "default",
            cancelButtonVariant: "outline",
            cancelLoading: isLoggingOutSession.value,
            confirmDisabled: isIdleActionLoading.value,
            cancelDisabled: isIdleActionLoading.value,
            onCancelModal: handleIdleSignOut,
            onConfirm: handleIdleStaySignedIn
          }, null, 8, ["subMessage", "cancelLoading", "confirmDisabled", "cancelDisabled"]))
        : _createCommentVNode("", true),
      (showTokenDialog.value)
        ? (_openBlock(), _createBlock(_unref(ConfirmDialog), {
            key: 1,
            title: "Your current session will expire soon",
            message: "To keep your account secure, your session will expire soon. Renew now to stay signed in.",
            subMessage: tokenDialogSubMessage.value,
            cancelText: "Log out now",
            confirmText: "Renew session",
            confirmButtonVariant: "default",
            cancelButtonVariant: "outline",
            confirmLoading: isRenewingSession.value,
            cancelLoading: isLoggingOutSession.value,
            confirmDisabled: isTokenActionLoading.value,
            cancelDisabled: isTokenActionLoading.value,
            onCancelModal: handleTokenSignOut,
            onConfirm: handleTokenRenew
          }, null, 8, ["subMessage", "confirmLoading", "cancelLoading", "confirmDisabled", "cancelDisabled"]))
        : _createCommentVNode("", true),
      _createVNode(_unref(Modal), {
        showModal: showTimeoutModal.value && timeoutModalEligible.value,
        closable: false,
        rounded: true,
        maxWidth: "28rem",
        onCloseModal: handleTimeoutModalClose
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_2, [
            _cache[1] || (_cache[1] = _createElementVNode("h4", { class: "mb-2" }, "Session Timeout!", -1)),
            _createElementVNode("p", _hoisted_3, _toDisplayString(timeoutMessage.value), 1),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(Button), {
                size: "sm",
                onClick: handleTimeoutModalClose
              }, {
                default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                  _createTextVNode(" Ok ", -1)
                ]))]),
                _: 1
              })
            ])
          ])
        ]),
        _: 1
      }, 8, ["showModal"])
    ])
  ], 64))
}
}

})